/**
 * Live session shell — matches pages/session-experience-redesign.html (room view).
 *
 * Phone (≤767 via useMobileExperience): single-column room — main canvas full
 * width, context/participants in a sheet, primary ranking CTA in a bottom dock.
 * Desktop keeps the main + 224px context rail.
 */

/* eslint-disable react-refresh/only-export-components */

import { useState, type ReactNode } from 'react';
import { Users, X } from 'lucide-react';
import { useMobileExperience } from '../../../../mobile/featureFlag';

/** Live session colors — the app tokens, so the session follows light (Paper) and dark (Night). */
export const live = {
  bg: 'bg-[var(--bg)]',
  raised: 'bg-[var(--bg-raised)]',
  surface: 'bg-[var(--surface)]',
  border: 'border-[var(--rule)]',
  borderBright: 'border-[var(--rule-bright)]',
  text: 'text-[var(--text-pri)]',
  textSec: 'text-[var(--text-sec)]',
  textTer: 'text-[var(--text-ter)]',
  textDim: 'text-[var(--text-dim)]',
} as const;

export function LiveSessionViewport({ children }: { children: ReactNode }) {
  const isMobile = useMobileExperience();
  return (
    <div
      className={`fixed inset-y-0 ${isMobile ? 'left-0' : 'left-64'} right-0 z-30 flex flex-col ${live.bg} ${live.text} font-body overflow-hidden`}
    >
      {children}
    </div>
  );
}

export interface LiveLayerBarProps {
  backLabel: string;
  onBack: () => void;
  title: string;
  subtitle?: string;
  trailing?: ReactNode;
}

export function LiveLayerBar({ backLabel, onBack, title, subtitle, trailing }: LiveLayerBarProps) {
  const isMobile = useMobileExperience();
  return (
    <div
      className={`flex items-stretch flex-shrink-0 ${isMobile ? 'min-h-[48px] h-auto' : 'h-[50px]'} ${live.raised} border-b ${live.border}`}
    >
      <button
        type="button"
        onClick={onBack}
        className={`font-heading text-[0.8rem] tracking-[0.1em] uppercase ${live.textTer} ${
          isMobile ? 'px-3 min-h-[48px]' : 'px-4'
        } border-r ${live.border} hover:bg-[var(--bg-raised)]/[0.04] transition-colors flex-shrink-0`}
      >
        {backLabel}
      </button>
      <div className={`flex flex-col justify-center ${isMobile ? 'px-3 py-2' : 'px-4'} border-r ${live.border} min-w-0 flex-1`}>
        <div className="font-display text-[1rem] text-[var(--text-pri)]/90 truncate leading-tight">{title}</div>
        {subtitle && (
          <div className={`font-heading text-[0.78rem] ${live.textTer} tracking-[0.06em] uppercase truncate`}>
            {subtitle}
          </div>
        )}
      </div>
      {trailing && (
        <div className={`flex items-center gap-2 ${isMobile ? 'px-2' : 'px-4'} ml-auto flex-shrink-0`}>
          {trailing}
        </div>
      )}
    </div>
  );
}

export function LiveStatusPulse({ label = 'Live' }: { label?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 font-heading text-[0.82rem] tracking-[0.08em] uppercase text-luminous-coral-700">
      <span className="w-[7px] h-[7px] rounded-full bg-luminous-coral-700 animate-pulse" />
      {label}
    </span>
  );
}

export function LiveProgressBar({ placed, total }: { placed: number; total: number }) {
  const pct = total > 0 ? Math.round((placed / total) * 100) : 0;
  return (
    <div className="mb-4">
      <div className={`flex justify-between font-heading text-[0.82rem] tracking-[0.04em] ${live.textSec} mb-2`}>
        <span>
          <span className="text-alpine-green-400 font-bold">{placed}</span> of {total} items placed
        </span>
        <span>{pct}% complete</span>
      </div>
      <div className={`h-1 ${live.borderBright} bg-[var(--rule-bright)] rounded-full overflow-hidden`}>
        <div
          className="h-full bg-alpine-green-500/75 rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export interface LiveParticipantRow {
  id: string;
  name: string;
  initials: string;
  submitted: boolean;
  isMe: boolean;
  progressPct?: number;
}

export interface LiveContextPanelProps {
  methodLabel: string;
  trackLabel?: string;
  itemCount: number;
  deadlineLabel?: string;
  participants: LiveParticipantRow[];
  submittedCount: number;
  totalCount: number;
  footer?: ReactNode;
  /** Full-width body when rendered inside the phone sheet. */
  variant?: 'rail' | 'sheet';
}

export function LiveContextPanel({
  methodLabel,
  trackLabel,
  itemCount,
  deadlineLabel,
  participants,
  submittedCount,
  totalCount,
  footer,
  variant = 'rail',
}: LiveContextPanelProps) {
  const body = (
    <>
      <div className="mb-5">
        <div className={`font-heading text-[0.78rem] font-semibold tracking-[0.1em] uppercase ${live.textTer} mb-2 pb-1.5 border-b ${live.border}`}>
          Session
        </div>
        <CtxRow label="Status" value={<span className="text-luminous-coral-700">Live</span>} />
        <CtxRow label="Method" value={methodLabel} />
        {trackLabel && <CtxRow label="Track" value={trackLabel} />}
        <CtxRow label="Items" value={String(itemCount)} />
        {deadlineLabel && (
          <CtxRow label="Deadline" value={<span className="text-luminous-coral-700">{deadlineLabel}</span>} />
        )}
      </div>

      <div>
        <div className={`font-heading text-[0.78rem] font-semibold tracking-[0.1em] uppercase ${live.textTer} mb-2 pb-1.5 border-b ${live.border}`}>
          Participants — {submittedCount}/{totalCount}
        </div>
        <div className="flex flex-col gap-1.5">
          {participants.map((p) => (
            <div key={p.id} className="flex items-center gap-2 min-w-0">
              <div
                className={`w-6 h-6 rounded-full flex items-center justify-center font-heading text-[0.72rem] font-bold flex-shrink-0 border ${
                  p.isMe
                    ? 'text-electric-mustard-600 border-electric-mustard-600/40 bg-electric-mustard-600/10'
                    : p.submitted
                      ? 'text-alpine-green-400 border-alpine-green-500/40 bg-alpine-green-500/10'
                      : `${live.textDim} border-[var(--rule-bright)] bg-[var(--bg-raised)]/[0.02]`
                }`}
              >
                {p.initials}
              </div>
              <span
                className={`font-body text-[0.85rem] flex-1 truncate ${
                  p.isMe ? 'text-electric-mustard-600 font-medium' : p.submitted ? live.textSec : live.textDim
                }`}
              >
                {p.isMe ? 'You' : p.name}
              </span>
              {p.isMe && p.progressPct != null ? (
                <span className="font-heading text-[0.78rem] text-electric-mustard-600">{p.progressPct}%</span>
              ) : (
                <span
                  className={`w-[6px] h-[6px] rounded-full flex-shrink-0 ${
                    p.submitted ? 'bg-alpine-green-400/80' : 'bg-[var(--text-dim)]/40'
                  }`}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {footer != null && (
        <div className={variant === 'rail' ? `mt-4 pt-3 border-t ${live.border}` : undefined}>
          {footer}
        </div>
      )}
    </>
  );

  if (variant === 'sheet') {
    return <div className="px-4 py-4 overflow-y-auto">{body}</div>;
  }

  return (
    <aside
      className={`w-[224px] flex-shrink-0 border-l ${live.border} ${live.raised} px-3.5 py-4 overflow-y-auto`}
    >
      {body}
    </aside>
  );
}

function CtxRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1 gap-2">
      <span className={`font-body text-[0.88rem] font-light ${live.textTer}`}>{label}</span>
      <span className={`font-heading text-[0.85rem] font-medium ${live.textSec}`}>{value}</span>
    </div>
  );
}

export function LiveRoomLayout({
  main,
  contextPanel,
  facilitatorBar,
  /** Phone: sticky bottom dock (Start ranking / submitted). */
  mobileDock,
  /** Phone: open session/people sheet from the layer bar. */
  peopleLabel,
}: {
  main: ReactNode;
  contextPanel: ReactNode;
  facilitatorBar?: ReactNode;
  mobileDock?: ReactNode;
  peopleLabel?: string;
}) {
  const isMobile = useMobileExperience();
  const [peopleOpen, setPeopleOpen] = useState(false);

  if (!isMobile) {
    return (
      <>
        {facilitatorBar}
        <div className="flex flex-1 min-h-0 overflow-hidden">
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden">{main}</div>
          {contextPanel}
        </div>
      </>
    );
  }

  return (
    <>
      {facilitatorBar}
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
        {(peopleLabel || contextPanel) && (
          <div className={`flex-shrink-0 flex items-center justify-end px-3 py-2 border-b ${live.border} ${live.raised}`}>
            <button
              type="button"
              onClick={() => setPeopleOpen(true)}
              className={`inline-flex items-center gap-1.5 min-h-[40px] px-3  border ${live.borderBright} font-heading text-[0.72rem] tracking-[0.08em] uppercase ${live.textSec}`}
            >
              <Users className="h-3.5 w-3.5" />
              {peopleLabel ?? 'People'}
            </button>
          </div>
        )}
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">{main}</div>
        {mobileDock != null && (
          <div
            className={`flex-shrink-0 border-t ${live.border} ${live.raised} px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]`}
          >
            {mobileDock}
          </div>
        )}
      </div>

      {peopleOpen && (
        <div className="fixed inset-0 z-[60] flex flex-col justify-end">
          <button
            type="button"
            aria-label="Close people"
            className="absolute inset-0 bg-black/55"
            onClick={() => setPeopleOpen(false)}
          />
          <div
            className={`relative max-h-[75vh] flex flex-col  border-t ${live.border} ${live.raised} overflow-hidden`}
          >
            <div className={`flex items-center justify-between px-4 py-3 border-b ${live.border}`}>
              <span className={`font-heading text-[0.75rem] tracking-[0.1em] uppercase ${live.textTer}`}>
                Session & people
              </span>
              <button
                type="button"
                onClick={() => setPeopleOpen(false)}
                className={`p-2  ${live.textTer}`}
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="overflow-y-auto min-h-0 flex-1">{contextPanel}</div>
          </div>
        </div>
      )}
    </>
  );
}

export function LiveFacilitatorBar({ children }: { children: ReactNode }) {
  const isMobile = useMobileExperience();
  return (
    <div
      className={`flex-shrink-0 ${live.raised} border-b ${live.border} ${
        isMobile
          ? 'px-3 py-2.5 flex flex-col gap-2'
          : 'px-5 py-2.5 flex items-center gap-4'
      }`}
    >
      {isMobile ? (
        <div className="flex flex-col gap-2 w-full min-w-0">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
